// @givmo/org-console — `OrgQrDesignEditor`, the ONE editor for an organization's QR design (FCM-WEB4;
// plan §FC-14, D-FCM-12 and its refinements).
//
// An organization has ONE saved default QR design and it governs every QR code the organization
// generates. This editor is where it is changed, and it is placement-agnostic: the Fundraisers module
// opens it for one fundraiser's share code, the Profile page opens it for the organization's public
// code (`target`). It edits a WORKING design:
//
//   * every preview and every download is a SERVER render of the working design (the target's
//     supplied-design op, which saves nothing) — there is no web QR renderer;
//   * only "Save as default" persists it (`PUT /org/{org_ref}/qr-design`), and it renders only for a
//     holder of `CAPABILITY_MANAGE_ORG_QR` (`canSave`, the host's `holdsCapability` off its ONE
//     standing read — who holds it is the backend's data, never a string this package names);
//     housed under Profile → Branding (`saveLine`), the same PUT goes out from the tab's own save
//     line at the top right (`OrgSaveLine`), so the tab has one save model;
//   * "Reset to Givmo standard" returns the WORKING design to the standard, behind the apps' own
//     confirm, and saves nothing.
//
// ⚠ PREVIEWS FOLLOW COMMITTED CHANGES — a picker closed, a slider released, a choice made, a hex
// value or a caption entered (blur or Enter) — debounced, one in flight, the one before it abandoned
// (its query loses its last observer and is cancelled). Dragging a slider moves only its readout, and
// typing moves only the field: every preview is a render the person's hourly budget pays for.
//
// ⚠ THE SERVER ALONE JUDGES A DESIGN. A 422 after a colour change keeps the last good preview,
// disables Save and Download, and shows the apps' colour sentence; any other 422 shows the reset line.
// Nothing here reads the server's words.
//
// Class words arrive as `OrgQrDesignVocab` (what saving affects, who may, the logo's owner) — the
// package never learns whose organization it is drawing for.

import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError, type QrDesignSpec } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorMessage,
  GivmoLogomark,
  Icon,
  Skeleton,
  Switch,
  TextField,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { AlertTriangle, Download, Info, RotateCcw } from 'lucide-react';
import { OrgConsoleUnavailableNotice, OrgNotPermittedNotice } from './org-console-notices';
import { OrgSaveLine } from './org-save-line';
import {
  QR_CAPTION_PLACEHOLDER,
  QR_DESIGN_STANDARD,
  QR_MAX_CAPTION_LENGTH,
  QR_MAX_LOGO_SCALE,
  QR_MIN_LOGO_SCALE,
  QR_STANDARD_LOGO_SCALE,
  parseHexColor,
  qrAngleReadout,
  qrDesignState,
  qrLogoScaleReadout,
  sameQrDesign,
  withBackground,
  withCaption,
  withForeground,
  withFrame,
  withGradient,
  withGradientAngle,
  withGradientStop,
  withLogoKind,
  withLogoScale,
  withModuleShape,
  type OrgQrDesignRead,
  type QrLogoKind,
  type QrModuleShape,
} from './org-qr-design-model';
import {
  ORG_QR_FORMATS,
  downloadSuppliedQr,
  orgPublicQrKey,
  orgQrDesignKey,
  orgQrDesignOptions,
  orgQrPreviewOptions,
  saveOrgQrDesign,
  type OrgQrFormat,
  type OrgQrTarget,
} from './org-qr-design-queries';

/** The host's words. */
export interface OrgQrDesignVocab {
  /** The organization's own name — the logo choice reads "{orgName} logo". */
  orgName: string;
  /** Beneath Save: what saving the default changes, in the host's words. */
  saveScope: string;
  /** For a person who cannot save: who can. Absent ⇒ nothing is said. */
  saveUnavailable?: string;
  /**
   * The public code's refusal while the organization is not yet in standing for its public target
   * (`ORG_SHARE_TARGET_UNAVAILABLE`), in the host's words.
   */
  targetUnavailable?: string;
}

export interface OrgQrDesignEditorProps {
  orgRef: string;
  target: OrgQrTarget;
  /** The host's `holdsCapability(capabilities, CAPABILITY_MANAGE_ORG_QR)`; false while unresolved. */
  canSave: boolean;
  vocab: OrgQrDesignVocab;
  /** The organization's logo for the logo choice's tile (the host already reads it); optional. */
  orgLogoSrc?: string | null;
  /**
   * Housed in a page with its own save line (Profile → Branding): `lede` and the save line
   * (`OrgSaveLine`: "All changes saved" / "Unsaved changes", Save) head the editor, as on the rest of
   * that page, and the actions card keeps only the downloads. Absent ⇒ the "Save as default" card.
   */
  saveLine?: { lede: ReactNode };
  /**
   * Told when the working design starts or stops holding a change not yet saved, for the host's
   * unsaved-changes guard. Pass a stable function.
   */
  onUnsavedChange?: (unsaved: boolean) => void;
  testIdPrefix: string;
}

/** The apps' colour sentence, verbatim (iOS `QrDesignValidator`, `insufficientContrast`). */
export const QR_COLOR_REFUSAL_LINE =
  'There isn’t enough contrast between the dots and the background to scan reliably. Darken the dots or lighten the background.';
/** Any other refusal of a design: the way out is the standard. */
export const QR_DESIGN_REFUSAL_LINE =
  'This design can’t be used. Reset it to the Givmo standard to continue.';
/** Under the preview's title: what it draws — the working design, never the saved one. */
const QR_PREVIEW_SUBTITLE = 'Shows your changes as you make them';
export const QR_STATE_LABELS = {
  standard: 'Givmo standard design',
  saved: 'Your organization’s saved design',
  unsaved: 'Unsaved changes',
} as const;
const SAVED_DESIGN_INVALID_LINE =
  'Your saved design can’t be used anymore, so your QR codes show the Givmo standard until a new design is saved.';
const PREVIEW_DEBOUNCE_MS = 250;

type ChangeKind = 'color' | 'other';
type Refusal = 'color' | 'other';

export function OrgQrDesignEditor(props: OrgQrDesignEditorProps) {
  const id = (name: string) => `${props.testIdPrefix}-${name}`;
  const design = useQuery({
    ...orgQrDesignOptions(props.orgRef),
    enabled: props.orgRef.length > 0,
  });
  if (!props.orgRef || design.isPending) {
    return (
      <div
        className="flex flex-col gap-16"
        role="status"
        aria-label="Loading the QR code design"
        {...testIdProp(id('qr-loading'))}
      >
        <Skeleton className="h-[320px] w-full rounded-xl" />
      </div>
    );
  }
  if (design.isError) {
    const status = isGivmoError(design.error) ? design.error.status : undefined;
    if (status === 404)
      return (
        <OrgConsoleUnavailableNotice
          testId={id('qr-unavailable')}
          title="QR code design"
          description="QR code design isn’t available for this organization here."
        />
      );
    if (status === 403)
      return (
        <OrgNotPermittedNotice
          testId={id('qr-locked')}
          title="QR code design"
          description="You don’t have access to this organization’s QR code design."
        />
      );
    return (
      <EmptyState
        testId={id('qr-error')}
        title="Couldn’t load your QR code design"
        description="Please try again."
        action={
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => void design.refetch()}
            testId={id('qr-retry')}
          >
            Try again
          </Button>
        }
      />
    );
  }
  return <LoadedEditor key={props.orgRef} read={design.data} {...props} />;
}

/** The latest value once it has held still for `ms`. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function refusalOf(error: unknown): 'design' | 'target' | 'absent' | 'busy' | 'failed' {
  if (!isGivmoError(error)) return 'failed';
  if (error.status === 422) return 'design';
  if (error.status === 409 && firstErrorCode(error) === 'ORG_SHARE_TARGET_UNAVAILABLE')
    return 'target';
  if (error.status === 404) return 'absent';
  if (error.status === 429) return 'busy';
  return 'failed';
}

function LoadedEditor({
  orgRef,
  target,
  canSave,
  vocab,
  orgLogoSrc,
  saveLine,
  onUnsavedChange,
  testIdPrefix,
  read,
}: OrgQrDesignEditorProps & { read: OrgQrDesignRead }) {
  const id = (name: string) => `${testIdPrefix}-${name}`;
  const queryClient = useQueryClient();
  const [working, setWorking] = useState<QrDesignSpec>(read.spec);
  const [committed, setCommitted] = useState<{ spec: QrDesignSpec; kind: ChangeKind }>({
    spec: read.spec,
    kind: 'other',
  });
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [downloading, setDownloading] = useState<OrgQrFormat | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  /** One control's edit; `commit` false moves the control (a readout, a swatch) but asks for no render. */
  const change = useCallback((next: QrDesignSpec, kind: ChangeKind, commit = true) => {
    setWorking(next);
    setSaveError(null);
    setExportError(null);
    if (commit) setCommitted({ spec: next, kind });
  }, []);
  /** Commit what a text field has been moving (a caption entered); nothing, if nothing moved. */
  const commitWorking = () => {
    if (!sameQrDesign(working, committed.spec)) change(working, 'other');
  };

  const settled = useSettled(committed, PREVIEW_DEBOUNCE_MS);
  const preview = useQuery(orgQrPreviewOptions(orgRef, target, settled.spec));
  const previewFailure = preview.isError ? refusalOf(preview.error) : null;

  useEffect(() => {
    if (preview.isSuccess) {
      setShown(preview.data);
      setRefusal(null);
    }
  }, [preview.isSuccess, preview.data]);
  useEffect(() => {
    if (previewFailure !== 'design') return;
    // A colour made it unscannable — or it still is, whatever was changed since.
    setRefusal((current) => (settled.kind === 'color' || current === 'color' ? 'color' : 'other'));
  }, [previewFailure, settled]);

  const state = qrDesignState(read, working);
  const blocked = refusal !== null || previewFailure === 'target' || previewFailure === 'absent';
  const savable =
    canSave &&
    !blocked &&
    (state === 'unsaved' || (read.savedDesignInvalid && state === 'standard'));

  const save = useMutation({
    mutationFn: () => saveOrgQrDesign(orgRef, committed.spec),
    onSuccess: async (answer) => {
      queryClient.setQueryData(orgQrDesignKey(orgRef), answer);
      await queryClient.invalidateQueries({ queryKey: orgPublicQrKey(orgRef) });
      setWorking(answer.spec);
      setCommitted({ spec: answer.spec, kind: 'other' });
      toast.success('Saved as your default design');
    },
    onError: (error) => {
      const kind = refusalOf(error);
      if (kind === 'design') {
        setRefusal(committed.kind === 'color' ? 'color' : 'other');
        return;
      }
      setSaveError(
        isGivmoError(error) && error.status === 403
          ? 'Saving the default design isn’t available to you.'
          : 'Couldn’t save the design. Nothing was changed — please try again.',
      );
    },
  });

  // Only a person's own edit is unsaved: a saved design the server can no longer use is savable (its
  // standard can replace it) but nothing the person would lose by leaving (review-polish B-1).
  const unsaved = state === 'unsaved' && !save.isPending;
  useEffect(() => {
    onUnsavedChange?.(unsaved);
  }, [unsaved, onUnsavedChange]);
  useEffect(() => () => onUnsavedChange?.(false), [onUnsavedChange]);

  async function download(format: OrgQrFormat) {
    setDownloading(format);
    setExportError(null);
    try {
      await downloadSuppliedQr(orgRef, target, committed.spec, format);
      toast.success('QR code downloaded');
    } catch (error) {
      if (refusalOf(error) === 'design') setRefusal(committed.kind === 'color' ? 'color' : 'other');
      else setExportError('Couldn’t download the QR code. Please try again.');
    } finally {
      setDownloading(null);
    }
  }

  const gradient = working.gradient ?? null;
  const logo = working.logo ?? QR_DESIGN_STANDARD.logo!;
  const frame = working.frame ?? QR_DESIGN_STANDARD.frame!;
  const colorRefused = refusal === 'color';

  const editor = (
    <div
      className="grid grid-cols-1 items-start gap-20 xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-24"
      {...testIdProp(id('qr-editor'))}
    >
      {/* Desktop: one aside, sticky. Below xl it dissolves, so the preview can lead — pinned under
          the console's 56px app bar in a full-bleed band of the page colour (the apps pin theirs
          too) — and the actions can follow the controls. The band's bleed matches the console
          page gutter (16px, 24px from sm). */}
      <div className="contents xl:sticky xl:top-[80px] xl:col-start-2 xl:row-start-1 xl:flex xl:flex-col xl:gap-20">
        <div className="sticky top-[56px] z-10 order-1 -mx-16 bg-surface-sunken px-16 pt-8 pb-12 sm:-mx-24 sm:px-24 xl:static xl:order-none xl:mx-0 xl:bg-transparent xl:p-0">
          <Card testId={id('qr-preview-card')}>
            <CardHeader
              title="Preview"
              subtitle={QR_PREVIEW_SUBTITLE}
              className="items-center max-xl:py-12"
              actions={
                saveLine !== undefined ? undefined : (
                  <span
                    className="flex items-center gap-6 text-s13 text-text-tertiary"
                    aria-live="polite"
                    {...testIdProp(id('qr-state'))}
                  >
                    {state === 'unsaved' && (
                      <span aria-hidden className="size-6 shrink-0 rounded-full bg-brand-500" />
                    )}
                    {QR_STATE_LABELS[state]}
                  </span>
                )
              }
            />
            <CardBody className="flex flex-col items-center gap-12 max-xl:py-12">
              <PreviewImage
                src={
                  previewFailure === 'target' || previewFailure === 'absent'
                    ? null
                    : (preview.data ?? shown)
                }
                failure={previewFailure}
                targetUnavailable={vocab.targetUnavailable}
                testId={id('qr-preview')}
              />
              {refusal !== null && shown !== null && (
                <p
                  className="text-center text-s13 text-text-secondary"
                  {...testIdProp(id('qr-stale'))}
                >
                  Showing the last design that scans.
                </p>
              )}
              {(previewFailure === 'busy' || previewFailure === 'failed') && (
                <p
                  className="text-center text-s13 text-text-secondary"
                  role="status"
                  {...testIdProp(id('qr-preview-error'))}
                >
                  {previewFailure === 'busy'
                    ? 'Previews are paused for a moment. Try again shortly.'
                    : 'Couldn’t update the preview. Your changes are kept.'}
                </p>
              )}
            </CardBody>
          </Card>
        </div>

        <Card className="order-3 xl:order-none" testId={id('qr-actions')}>
          <CardBody className="flex flex-col gap-16">
            {saveLine !== undefined ? null : canSave ? (
              <div className="flex flex-col gap-8">
                <Button
                  appearance="saas"
                  size="lg"
                  className="w-full max-lg:min-h-44"
                  disabled={!savable}
                  loading={save.isPending}
                  onClick={() => save.mutate()}
                  testId={id('qr-save-button')}
                >
                  Save as default
                </Button>
                <p className="text-s13 text-text-tertiary" {...testIdProp(id('qr-save-scope'))}>
                  {vocab.saveScope}
                </p>
                {saveError !== null && (
                  <ErrorMessage testId={id('qr-save-error')}>{saveError}</ErrorMessage>
                )}
              </div>
            ) : vocab.saveUnavailable ? (
              <p
                className="text-s13 text-text-secondary"
                {...testIdProp(id('qr-save-unavailable'))}
              >
                {vocab.saveUnavailable}
              </p>
            ) : null}
            <div
              className={cn(
                'flex flex-col gap-8',
                canSave && saveLine === undefined && 'border-t border-border-subtle pt-16',
              )}
            >
              <p className="text-s13 font-w600 text-text-strong">Download this design</p>
              <div className="flex flex-wrap items-center gap-8">
                {ORG_QR_FORMATS.map((format) => (
                  <Button
                    key={format}
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
                    className="max-lg:min-h-44"
                    disabled={blocked || (downloading !== null && downloading !== format)}
                    loading={downloading === format}
                    onClick={() => void download(format)}
                    testId={id(`qr-export-${format}`)}
                  >
                    {format.toUpperCase()}
                  </Button>
                ))}
              </div>
              <p className="text-s13 text-text-tertiary">
                Downloading doesn’t change your saved design.
              </p>
              {exportError !== null && (
                <ErrorMessage testId={id('qr-export-error')}>{exportError}</ErrorMessage>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      <Card
        className="order-2 xl:order-none xl:col-start-1 xl:row-start-1"
        testId={id('qr-controls')}
      >
        <CardHeader
          title="Design"
          className="items-center"
          actions={
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              leadingIcon={<Icon icon={RotateCcw} size={14} aria-hidden />}
              className="max-lg:min-h-44"
              onClick={() => setConfirmReset(true)}
              testId={id('qr-reset-button')}
            >
              Reset to Givmo standard
            </Button>
          }
        />
        <CardBody flush className="divide-y divide-border-subtle">
          {read.savedDesignInvalid && (
            <div className="px-20 py-16">
              <p
                className="flex items-start gap-8 rounded-md border border-border bg-surface-sunken px-12 py-10 text-s13 text-text"
                {...testIdProp(id('qr-saved-invalid'))}
              >
                <Icon icon={Info} size={16} aria-hidden className="mt-2 shrink-0 text-icon" />
                {SAVED_DESIGN_INVALID_LINE}
              </p>
            </div>
          )}
          <Section title="Shape">
            <Choice
              name={id('qr-shape')}
              label="Shape"
              value={working.module_shape ?? 'square'}
              onChange={(shape) =>
                change(withModuleShape(working, shape as QrModuleShape), 'other')
              }
              options={[
                { value: 'square', label: 'Square', art: <ShapeArt shape="square" /> },
                { value: 'rounded', label: 'Rounded', art: <ShapeArt shape="rounded" /> },
                { value: 'circle', label: 'Circle', art: <ShapeArt shape="circle" /> },
              ]}
              testId={(value) => id(`qr-shape-${value}`)}
            />
            <Hint>
              {working.module_shape === 'circle'
                ? 'Circle dots can be harder for some scanners — test a print before sharing widely.'
                : 'The three corner squares stay solid in every style, so the code scans.'}
            </Hint>
          </Section>

          <Section title="Colors">
            {/* The dots colour is scored even while a gradient paints the dots, so it reappears
                whenever a colour refusal stands — never a refusal the editor offers no way out of. */}
            {(gradient === null || colorRefused) && (
              <ColorRow
                label="Dots"
                value={working.fg_color}
                invalid={colorRefused}
                onInput={(hex) => change(withForeground(working, hex), 'color', false)}
                onCommit={(hex) => change(withForeground(working, hex), 'color')}
                testId={id('qr-fg-color')}
              />
            )}
            <ColorRow
              label="Background"
              value={working.bg_color}
              onInput={(hex) => change(withBackground(working, hex), 'color', false)}
              onCommit={(hex) => change(withBackground(working, hex), 'color')}
              testId={id('qr-bg-color')}
            />
            <SwitchRow
              label="Use a gradient"
              checked={gradient !== null}
              onChange={(on) => change(withGradient(working, on), 'color')}
              testId={id('qr-gradient-toggle')}
            />
            {gradient !== null && (
              <Dependent>
                <ColorRow
                  label="From"
                  value={gradient.from_color}
                  invalid={colorRefused}
                  onInput={(hex) =>
                    change(withGradientStop(working, 'from_color', hex), 'color', false)
                  }
                  onCommit={(hex) => change(withGradientStop(working, 'from_color', hex), 'color')}
                  testId={id('qr-gradient-from')}
                />
                <ColorRow
                  label="To"
                  value={gradient.to_color}
                  invalid={colorRefused}
                  onInput={(hex) =>
                    change(withGradientStop(working, 'to_color', hex), 'color', false)
                  }
                  onCommit={(hex) => change(withGradientStop(working, 'to_color', hex), 'color')}
                  testId={id('qr-gradient-to')}
                />
                <RangeRow
                  label="Angle"
                  min={0}
                  max={360}
                  step={1}
                  value={gradient.angle ?? 0}
                  readout={qrAngleReadout(gradient.angle)}
                  onInput={(angle) => change(withGradientAngle(working, angle), 'other', false)}
                  onCommit={(angle) => change(withGradientAngle(working, angle), 'other')}
                  testId={id('qr-gradient-angle')}
                />
              </Dependent>
            )}
            {refusal !== null && (
              <p
                role="alert"
                className="flex items-start gap-8 rounded-md border border-warning-100 bg-warning-50 px-12 py-10 text-s13 text-text"
                {...testIdProp(id(colorRefused ? 'qr-contrast-error' : 'qr-design-error'))}
              >
                <Icon
                  icon={AlertTriangle}
                  size={16}
                  aria-hidden
                  className="mt-2 shrink-0 text-warning-700"
                />
                {colorRefused ? QR_COLOR_REFUSAL_LINE : QR_DESIGN_REFUSAL_LINE}
              </p>
            )}
          </Section>

          <Section title="Logo">
            {read.orgLogoAvailable ? (
              <Choice
                name={id('qr-logo')}
                label="Logo"
                value={logo.kind === 'entity' ? 'entity' : 'givmo'}
                onChange={(kind) => change(withLogoKind(working, kind as QrLogoKind), 'other')}
                options={[
                  {
                    value: 'givmo',
                    label: 'Givmo',
                    art: <GivmoLogomark aria-hidden className="size-20 text-brand-500" />,
                  },
                  {
                    value: 'entity',
                    label: `${vocab.orgName} logo`,
                    art: orgLogoSrc ? (
                      <img src={orgLogoSrc} alt="" className="size-20 object-contain" />
                    ) : null,
                  },
                ]}
                columns={2}
                testId={(value) => id(value === 'entity' ? 'qr-logo-entity' : 'qr-logo-givmo')}
              />
            ) : (
              <p className="text-s14 text-text" {...testIdProp(id('qr-logo-givmo'))}>
                Givmo
              </p>
            )}
            <RangeRow
              label="Logo size"
              min={QR_MIN_LOGO_SCALE}
              max={QR_MAX_LOGO_SCALE}
              step={0.01}
              value={logo.scale ?? QR_STANDARD_LOGO_SCALE}
              readout={qrLogoScaleReadout(logo.scale)}
              onInput={(scale) => change(withLogoScale(working, scale), 'other', false)}
              onCommit={(scale) => change(withLogoScale(working, scale), 'other')}
              testId={id('qr-logo-scale')}
            />
          </Section>

          <Section title="Frame">
            <SwitchRow
              label="Show a frame"
              checked={frame.enabled === true}
              onChange={(on) => change(withFrame(working, on), 'other')}
              testId={id('qr-frame-toggle')}
            />
            {frame.enabled === true && (
              <Dependent>
                <TextField
                  label="Caption (optional)"
                  placeholder={QR_CAPTION_PLACEHOLDER}
                  maxLength={QR_MAX_CAPTION_LENGTH}
                  value={frame.caption ?? ''}
                  onChange={(event) =>
                    change(withCaption(working, event.target.value), 'other', false)
                  }
                  onBlur={commitWorking}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') commitWorking();
                  }}
                  testId={id('qr-frame-caption')}
                />
              </Dependent>
            )}
          </Section>
        </CardBody>
      </Card>

      <Dialog.Root open={confirmReset} onOpenChange={setConfirmReset}>
        <Dialog.Content
          aria-label="Reset design to default"
          className="flex flex-col gap-16"
          testId={id('qr-reset-dialog')}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Reset design to default?
          </Dialog.Title>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            This clears your changes and returns to the standard Givmo design.
          </p>
          <div className="mt-8 flex items-center justify-end gap-8">
            <Dialog.Close
              render={
                <Button appearance="saas" tone="secondary" size="md" className="max-lg:min-h-44" />
              }
              {...testIdProp(id('qr-reset-cancel'))}
            >
              Cancel
            </Dialog.Close>
            <Button
              appearance="saas"
              size="md"
              className="max-lg:min-h-44"
              onClick={() => {
                change(QR_DESIGN_STANDARD, 'other');
                setConfirmReset(false);
              }}
              testId={id('qr-reset-confirm')}
            >
              Reset
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );

  if (saveLine === undefined) return editor;
  // Housed: the page's save line heads the editor. A design the server refused still counts as
  // unsaved, with Save held back until it scans. A saved design it can no longer use reads as the
  // standard in use (the card below says why), with Save offered to replace it.
  const lineState = save.isPending ? 'saving' : state === 'unsaved' ? 'unsaved' : 'saved';
  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-wrap items-center justify-between gap-12">
        <p className="font-sans text-s14 text-text-secondary">{saveLine.lede}</p>
        {canSave ? (
          <OrgSaveLine
            state={lineState}
            savedLabel={state === 'standard' ? QR_STANDARD_IN_USE : undefined}
            canSave={savable}
            onSave={() => save.mutate()}
            testIdPrefix={id('qr')}
          />
        ) : vocab.saveUnavailable ? (
          <p
            className="font-sans text-s13 text-text-tertiary"
            {...testIdProp(id('qr-save-unavailable'))}
          >
            {vocab.saveUnavailable}
          </p>
        ) : null}
      </div>
      {saveError !== null && <ErrorMessage testId={id('qr-save-error')}>{saveError}</ErrorMessage>}
      {editor}
    </div>
  );
}

/** The save line's "nothing to save" while the organization uses the standard and never saved one. */
const QR_STANDARD_IN_USE = 'Using the Givmo standard design';

// ── Building blocks ──────────────────────────────────────────────────────────────────────────────

function PreviewImage({
  src,
  failure,
  targetUnavailable,
  testId,
}: {
  src: string | null;
  failure: ReturnType<typeof refusalOf> | null;
  targetUnavailable?: string;
  testId: string;
}) {
  const box = 'w-full max-w-[252px] max-xl:max-w-[168px]';
  if (failure === 'target' || failure === 'absent') {
    return (
      <p
        className={cn(
          box,
          'flex aspect-square items-center justify-center rounded-md border border-dashed border-border px-16 text-center text-s13 text-text-secondary',
        )}
        {...testIdProp(`${testId}-unavailable`)}
      >
        {failure === 'target' && targetUnavailable
          ? targetUnavailable
          : 'This QR code isn’t available right now.'}
      </p>
    );
  }
  if (src === null && failure !== null) {
    return (
      <p
        className={cn(
          box,
          'flex aspect-square items-center justify-center rounded-md border border-dashed border-border px-16 text-center text-s13 text-text-secondary',
        )}
        {...testIdProp(`${testId}-unavailable`)}
      >
        The preview isn’t available right now.
      </p>
    );
  }
  if (src === null) return <Skeleton className={cn(box, 'aspect-square')} />;
  return (
    <img
      src={src}
      alt="QR code preview"
      className={cn(box, 'block h-auto')}
      {...testIdProp(testId)}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid grid-cols-1 gap-12 px-20 py-20 sm:grid-cols-[120px_minmax(0,1fr)] sm:gap-20">
      <h3 className="pt-6 text-s14 font-w600 text-text-strong">{title}</h3>
      <div className="flex min-w-0 flex-col gap-14 sm:max-w-[560px]">{children}</div>
    </section>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-s13 text-text-tertiary">{children}</p>;
}

/** A dependent group: marked by a leading rule — never a fill, never indent alone. */
function Dependent({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-14 border-l-2 border-border pl-16">{children}</div>;
}

function Choice({
  name,
  label,
  value,
  options,
  onChange,
  columns = 3,
  testId,
}: {
  name: string;
  label: string;
  value: string;
  options: readonly { value: string; label: string; art: ReactNode }[];
  onChange: (value: string) => void;
  columns?: 2 | 3;
  testId: (value: string) => string;
}) {
  return (
    <fieldset
      className={cn('grid gap-8', columns === 3 ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}
    >
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'flex min-h-44 cursor-pointer items-center gap-10 rounded-md border px-12 py-8 text-s14 transition-colors',
            'has-[:focus-visible]:shadow-ring',
            option.value === value
              ? 'border-brand-500 bg-brand-50 font-w600 text-text-strong'
              : 'border-border-strong bg-surface text-text hover:bg-surface-hover',
            columns === 3 && 'max-sm:flex-col max-sm:justify-center max-sm:gap-6 max-sm:px-8',
          )}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            className="sr-only"
            {...testIdProp(testId(option.value))}
          />
          {option.art && <span className="flex shrink-0 items-center">{option.art}</span>}
          <span className="min-w-0 truncate">{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** A 3×3 swatch of the module shape — what the choice draws, not a word for it. */
function ShapeArt({ shape }: { shape: QrModuleShape }) {
  const cells = [
    [0, 0],
    [2, 0],
    [1, 1],
    [2, 1],
    [0, 2],
    [1, 2],
  ] as const;
  return (
    <svg viewBox="0 0 18 18" className="size-18 text-text-strong" aria-hidden>
      {cells.map(([x, y]) =>
        shape === 'circle' ? (
          <circle key={`${x}${y}`} cx={x * 6 + 3} cy={y * 6 + 3} r={2.6} fill="currentColor" />
        ) : (
          <rect
            key={`${x}${y}`}
            x={x * 6 + 0.4}
            y={y * 6 + 0.4}
            width={5.2}
            height={5.2}
            rx={shape === 'rounded' ? 1.9 : 0}
            fill="currentColor"
          />
        ),
      )}
    </svg>
  );
}

/** The input's own `change` event — a picker closed, a slider released, a key pressed on it. */
function useCommitOnChange(
  ref: RefObject<HTMLInputElement | null>,
  onCommit: (value: string) => void,
) {
  const latest = useRef(onCommit);
  latest.current = onCommit;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const handler = () => latest.current(element.value);
    element.addEventListener('change', handler);
    return () => element.removeEventListener('change', handler);
  }, [ref]);
}

function ColorRow({
  label,
  value,
  onInput,
  onCommit,
  invalid = false,
  testId,
}: {
  label: string;
  value: string;
  onInput: (hex: string) => void;
  onCommit: (hex: string) => void;
  invalid?: boolean;
  testId: string;
}) {
  const swatch = useRef<HTMLInputElement>(null);
  useCommitOnChange(swatch, (hex) => {
    const parsed = parseHexColor(hex);
    if (parsed) onCommit(parsed);
  });
  const [draft, setDraft] = useState(value.toUpperCase());
  useEffect(() => setDraft(value.toUpperCase()), [value]);
  const commitDraft = () => {
    const parsed = parseHexColor(draft);
    if (parsed) onCommit(parsed);
    else setDraft(value.toUpperCase());
  };
  return (
    <div className="flex items-center gap-12">
      <span className="w-[88px] shrink-0 text-s14 text-text">{label}</span>
      <input
        ref={swatch}
        type="color"
        aria-label={`${label} color`}
        value={value.toLowerCase()}
        onChange={(event) => {
          const parsed = parseHexColor(event.target.value);
          if (parsed) onInput(parsed);
        }}
        className={cn(
          'size-32 shrink-0 cursor-pointer rounded-md border bg-surface p-2 max-lg:size-44',
          '[&::-webkit-color-swatch]:rounded-[4px] [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0',
          invalid ? 'border-warning-600' : 'border-border-strong',
        )}
        {...testIdProp(testId)}
      />
      <input
        type="text"
        aria-label={`${label} hex value`}
        aria-invalid={invalid || undefined}
        value={draft}
        maxLength={7}
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => setDraft(event.target.value.toUpperCase())}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commitDraft();
        }}
        className={cn(
          'h-32 w-[92px] rounded-md border bg-surface px-10 text-s13 tabular-nums text-text-strong outline-none focus:border-brand-500 focus:shadow-ring max-lg:h-44',
          invalid ? 'border-warning-600' : 'border-border-strong',
        )}
        {...testIdProp(`${testId}-hex`)}
      />
    </div>
  );
}

function RangeRow({
  label,
  min,
  max,
  step,
  value,
  readout,
  onInput,
  onCommit,
  testId,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  readout: string;
  onInput: (value: number) => void;
  onCommit: (value: number) => void;
  testId: string;
}) {
  const slider = useRef<HTMLInputElement>(null);
  useCommitOnChange(slider, (raw) => {
    const next = Number(raw);
    if (Number.isFinite(next)) onCommit(next);
  });
  return (
    <label className="flex items-center gap-12">
      <span className="w-[88px] shrink-0 text-s14 text-text">{label}</span>
      <input
        ref={slider}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={readout}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onInput(next);
        }}
        className="h-32 min-w-0 flex-1 cursor-pointer accent-brand-600 max-lg:h-44"
        {...testIdProp(testId)}
      />
      <span
        aria-hidden
        className="w-[40px] shrink-0 text-right text-s13 tabular-nums text-text-secondary"
      >
        {readout}
      </span>
    </label>
  );
}

function SwitchRow({
  label,
  checked,
  onChange,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
}) {
  return (
    <div className="flex min-h-32 items-center justify-between gap-12">
      <span className="text-s14 text-text">{label}</span>
      <Switch
        appearance="saas"
        aria-label={label}
        checked={checked}
        onCheckedChange={onChange}
        className="max-lg:after:absolute max-lg:after:-inset-x-4 max-lg:after:-inset-y-12 max-lg:after:content-['']"
        testId={testId}
      />
    </div>
  );
}
